import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * Self-contained horizontal scroll-snap carousel for the video carousel section.
 *
 * Purpose-built for this section instead of reusing the shared slideshow-component:
 * that component's "which slide is centered" math uses the scroller's left edge
 * (not its center) whenever more than one slide is visible at once, and its
 * infinite-loop reorder trick only supports single-visible-slide carousels — both
 * are the wrong fit for a 5-visible-on-desktop video grid. This element re-derives
 * "closest to center" from scratch and implements a real infinite loop via a
 * triple-cloned track (clone / originals / clone): whenever the card nearest the
 * viewport's center turns out to be a clone, it's resolved back to the real card
 * it represents and the scroll position is silently (no animation) recentered on
 * that real element — since clone and real are pixel-identical, the jump is
 * invisible, and it guarantees the "active"/playing card is always the one
 * actually on screen, not a same-looking real card sitting elsewhere off-screen.
 *
 * Dispatches the same `slideshow:select` CustomEvent (bubbles, `detail.slide`) the
 * shared slideshow-component used, so VideoCarouselComponent needs no changes to
 * consume it.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} scroller - The scrollable flex row containing cloned + real cards.
 *
 * @extends Component<Refs>
 */
export class VideoCarouselTrack extends Component {
  requiredRefs = ['scroller'];

  /** @type {HTMLElement[]} */
  #originals = [];

  /** @type {HTMLElement | null} */
  #currentActive = null;

  /** @type {{ pointerId: number, startX: number, startScrollLeft: number } | null} */
  #dragState = null;

  /** @type {boolean} */
  #didDrag = false;

  connectedCallback() {
    super.connectedCallback();
    this.#originals = this.#buildClones();
    if (this.#originals.length === 0) return;

    // Defer initial measurement/positioning a frame so layout (card widths,
    // scrollWidth) is fully settled first — mirrors the same Safari mobile
    // race-condition guard the shared slideshow component uses.
    requestAnimationFrame(() => {
      const initialIndex = this.#clampIndex(Number(this.getAttribute('initial-slide')) || 0);
      const initialCard = this.#originals[initialIndex];
      if (!initialCard) return;

      this.#jumpTo(initialCard);
      this.#setActive(initialCard);
    });

    const { scroller } = this.refs;
    scroller.addEventListener('scroll', this.#handleScroll, { passive: true });
    scroller.addEventListener('pointerdown', this.#handlePointerDown);
    scroller.addEventListener('pointermove', this.#handlePointerMove);
    scroller.addEventListener('pointerup', this.#handlePointerUp);
    scroller.addEventListener('pointercancel', this.#handlePointerUp);
    scroller.addEventListener('click', this.#handleCardClick);
    window.addEventListener('resize', this.#handleResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const { scroller } = this.refs;
    scroller?.removeEventListener('scroll', this.#handleScroll);
    scroller?.removeEventListener('pointerdown', this.#handlePointerDown);
    scroller?.removeEventListener('pointermove', this.#handlePointerMove);
    scroller?.removeEventListener('pointerup', this.#handlePointerUp);
    scroller?.removeEventListener('pointercancel', this.#handlePointerUp);
    scroller?.removeEventListener('click', this.#handleCardClick);
    window.removeEventListener('resize', this.#handleResize);
  }

  /**
   * Mouse-only click-and-drag-to-scroll. Native touch/trackpad scrolling
   * already works via the browser and is left untouched (gated on
   * pointerType === 'mouse'). Skips drags starting on a button (mute/play)
   * so those keep working as plain clicks.
   * @param {PointerEvent} event
   */
  #handlePointerDown = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    if (/** @type {HTMLElement} */ (event.target).closest('button')) return;

    this.#dragState = { pointerId: event.pointerId, startX: event.clientX, startScrollLeft: this.refs.scroller.scrollLeft };
    this.#didDrag = false;
  };

  /** @param {PointerEvent} event */
  #handlePointerMove = (event) => {
    const drag = this.#dragState;
    if (!drag || event.pointerId !== drag.pointerId) return;

    const deltaX = event.clientX - drag.startX;

    if (!this.#didDrag) {
      if (Math.abs(deltaX) < 4) return;
      this.#didDrag = true;
      this.refs.scroller.setPointerCapture(event.pointerId);
      this.refs.scroller.classList.add('is-dragging');
    }

    event.preventDefault();
    this.refs.scroller.scrollLeft = drag.startScrollLeft - deltaX;
  };

  /** @param {PointerEvent} event */
  #handlePointerUp = (event) => {
    const drag = this.#dragState;
    if (!drag || event.pointerId !== drag.pointerId) return;

    if (this.#didDrag) {
      if (this.refs.scroller.hasPointerCapture(event.pointerId)) {
        this.refs.scroller.releasePointerCapture(event.pointerId);
      }
      this.refs.scroller.classList.remove('is-dragging');
      // Consumed by #handleCardClick (fires right after) and by
      // VideoCarouselComponent#togglePlay, so a drag never also gets
      // interpreted as "select this card" or "toggle play on this card".
      this.refs.scroller.dataset.justDragged = 'true';
    }

    this.#dragState = null;
  };

  /**
   * Clicking any visible card (active or not) selects it: real or clone is
   * resolved to the one real card, which is smoothly scrolled to center and
   * activated immediately (not waiting for the scroll to finish) so playback
   * starts right away, matching the scroll animation catching up visually.
   * Clicking the already-active card is left alone — VideoCarouselComponent's
   * togglePlay (wired on the media element) owns play/pause for that case.
   * @param {MouseEvent} event
   */
  #handleCardClick = (event) => {
    const { scroller } = this.refs;
    const wasDrag = this.#didDrag || scroller.dataset.justDragged === 'true';
    this.#didDrag = false;
    delete scroller.dataset.justDragged;
    if (wasDrag) return;

    const target = /** @type {HTMLElement} */ (event.target).closest('[data-video-card]');
    if (!target) return;

    const real = target.hasAttribute('data-clone') ? this.#resolveReal(target) : target;
    if (!real || real === this.#currentActive) return;

    this.#setActive(real);
    this.#scrollToCenterSmooth(real);
  };

  /** Scrolls forward by one card. Wired to the next arrow via on:click. */
  next() {
    const step = this.#step();
    console.log('[video-carousel-track] next() — scrolling by', step, 'from', this.refs.scroller.scrollLeft);
    this.refs.scroller.scrollBy({ left: step, behavior: 'smooth' });
  }

  /** Scrolls backward by one card. Wired to the previous arrow via on:click. */
  previous() {
    const step = this.#step();
    console.log('[video-carousel-track] previous() — scrolling by', -step, 'from', this.refs.scroller.scrollLeft);
    this.refs.scroller.scrollBy({ left: -step, behavior: 'smooth' });
  }

  /**
   * Left-edge-to-left-edge distance between two adjacent cards (includes the
   * gap), measured live so it stays correct across breakpoints/peek settings
   * without duplicating the width formula from the stylesheet.
   * @returns {number}
   */
  #step() {
    const cards = this.#allCards();
    const first = cards[0];
    const second = cards[1];
    if (!first) return 0;
    if (!second) return first.getBoundingClientRect().width;

    return second.getBoundingClientRect().left - first.getBoundingClientRect().left;
  }

  /**
   * Clones the real (server-rendered) cards once before and once after
   * themselves, so the track reads as [clone][originals][clone]. Clones are
   * marked inert/aria-hidden and tagged data-clone so they never end up
   * driving the app (a duplicate Shopify editor block id must never be
   * treated as "the" active card). Every card — real or clone — gets a
   * data-source-index back to its position in the original set, so any
   * card found "closest to center" resolves to the one real element.
   *
   * @returns {HTMLElement[]} the real (non-clone) cards, in DOM order.
   */
  #buildClones() {
    const { scroller } = this.refs;
    const originals = /** @type {HTMLElement[]} */ (
      Array.from(scroller.querySelectorAll(':scope > [data-video-card]'))
    );
    if (originals.length === 0) return [];

    originals.forEach((card, index) => card.setAttribute('data-source-index', String(index)));

    const buildClone = () => {
      const fragment = document.createDocumentFragment();
      for (const card of originals) {
        const clone = /** @type {HTMLElement} */ (card.cloneNode(true));
        clone.setAttribute('data-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('inert', '');
        clone.removeAttribute('ref');
        fragment.appendChild(clone);
      }
      return fragment;
    };

    scroller.insertBefore(buildClone(), scroller.firstElementChild);
    scroller.appendChild(buildClone());

    return originals;
  }

  #handleScroll = debounce(() => {
    const closest = this.#findClosestToCenter();
    const active = this.#resolveReal(closest) ?? this.#originals[0];
    const closestIsClone = closest?.hasAttribute('data-clone') ?? false;
    console.log('[video-carousel-track] #handleScroll settled', {
      scrollLeft: this.refs.scroller.scrollLeft,
      closestIsClone,
      closestSourceIndex: closest?.getAttribute('data-source-index'),
      resolvedActiveSourceIndex: active?.getAttribute('data-source-index'),
    });
    if (active) this.#setActive(active);

    // A clone ended up visually centered (we've scrolled into a buffer copy).
    // Silently (no animation) recenter on the real card it represents, so
    // what's playing is always what's actually on screen — this is the
    // infinite-loop illusion: the clone and real card are pixel-identical,
    // so the jump is imperceptible.
    if (closestIsClone && active) {
      console.log('[video-carousel-track] closest was a clone — recentering on real index', active.getAttribute('data-source-index'));
      this.#jumpTo(active);
    }
  }, 120);

  #handleResize = debounce(() => {
    if (this.#originals.length === 0) return;
    this.#jumpTo(this.#currentActive ?? this.#originals[0]);
  }, 200);

  /** @returns {HTMLElement[]} every card currently in the track, clones included. */
  #allCards() {
    return /** @type {HTMLElement[]} */ (Array.from(this.refs.scroller.querySelectorAll('[data-video-card]')));
  }

  /** @returns {HTMLElement | null} whichever card (real or clone) is nearest the scroller's horizontal center. */
  #findClosestToCenter() {
    const { scroller } = this.refs;
    const scrollerRect = scroller.getBoundingClientRect();
    const viewportCenter = scrollerRect.left + scrollerRect.width / 2;

    /** @type {HTMLElement | null} */
    let closest = null;
    let closestDistance = Infinity;

    for (const card of this.#allCards()) {
      const rect = card.getBoundingClientRect();
      const cardCenter = rect.left + rect.width / 2;
      const distance = Math.abs(cardCenter - viewportCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        closest = card;
      }
    }

    return closest;
  }

  /**
   * @param {HTMLElement | null} card - a real or clone card.
   * @returns {HTMLElement | null} the corresponding real card.
   */
  #resolveReal(card) {
    if (!card) return null;
    const index = Number(card.getAttribute('data-source-index'));
    return this.#originals[index] ?? null;
  }

  /**
   * How far scrollLeft needs to move for `card` to sit centered in the
   * scroller, based on current (live) positions.
   * @param {HTMLElement} card
   * @returns {number}
   */
  #centerOffsetDelta(card) {
    const { scroller } = this.refs;
    const scrollerRect = scroller.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const cardCenter = cardRect.left + cardRect.width / 2 - scrollerRect.left;

    return cardCenter - scrollerRect.width / 2;
  }

  /**
   * Scrolls (instantly, no animation) so `card` sits centered in the scroller.
   * @param {HTMLElement} card
   */
  #jumpTo(card) {
    this.refs.scroller.scrollLeft += this.#centerOffsetDelta(card);
  }

  /**
   * Smoothly animates the scroll so `card` sits centered — used when a
   * card two (or more) slides away from center is clicked directly.
   * @param {HTMLElement} card
   */
  #scrollToCenterSmooth(card) {
    this.refs.scroller.scrollBy({ left: this.#centerOffsetDelta(card), behavior: 'smooth' });
  }

  /** @param {number} index */
  #clampIndex(index) {
    const count = this.#originals.length;
    if (count === 0) return 0;
    return ((index % count) + count) % count;
  }

  /** @param {HTMLElement} card - must be a real (non-clone) card. */
  #setActive(card) {
    if (this.#currentActive === card) {
      console.log('[video-carousel-track] #setActive — no-op, already active', card.getAttribute('data-source-index'));
      return;
    }
    console.log('[video-carousel-track] #setActive — dispatching slideshow:select for index', card.getAttribute('data-source-index'));
    this.#currentActive = card;
    this.dispatchEvent(new CustomEvent('slideshow:select', { bubbles: true, detail: { slide: card } }));
  }
}

if (!customElements.get('video-carousel-track')) {
  customElements.define('video-carousel-track', VideoCarouselTrack);
}
